import * as z from 'zod';

export const ReportEventRequest = z.object({
  triggerKey: z
    .string()
    .describe(
      "Trigger key as defined in your app's trigger configuration\nin the app dashboard.\nFor example, `form_submitted` or `invoice_due`."
    )
    .min(1)
    .max(100),
  options: z
    .object({
      payload: z
        .record(z.string(), z.any())
        .describe(
          'Event payload, formatted as key:value pairs.\nMust comply with the payload schema\nif you provided one when configuring your trigger.\n\nKey names can include only alphanumeric characters or underscores\n(`A-Z`, `a-z`, `0-9`, `_`).\nThey cannot start with an underscore.\n\nValues can be strings, numbers, integers, booleans, or arrays.\nIf a value is an array, the array items must be objects,\nand nested object properties must be\nstrings, numbers, integers, or booleans only.'
        )
        .optional()
        .nullable(),
      externalEntityId: z
        .string()
        .describe(
          'ID of the related resource in GUID format.\nFor example, `fc81a355-3429-50fc-a4c7-def486e828f3`.\n\nRequired if your app needs to\n[cancel the event](https://dev.wix.com/docs/rest/business-management/automations/triggered-events/cancel-event)\nif the automation becomes no longer relevant.\n\nTypically, this ID is defined in your system,\nbut you can also use any Wix resource GUID,\nsuch as contact ID, member ID, or invoice ID.\nSee\n[Choose the right `externalEntityId`](https://dev.wix.com/docs/rest/business-management/automations/triggered-events/reporting-and-canceling-events#about-canceling-events)\nfor more information.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      idempotency: z
        .object({
          key: z
            .string()
            .describe(
              'A unique identifier for the event.\nIf you send the same idempotency key in multiple report event requests, for the same trigger key and app id,\nconsecutive requests will be ignored after the first one. Note that the idempotency key is kept for a week before it expires.'
            )
            .min(1)
            .max(80)
            .optional(),
          ttlInMilliseconds: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Optional. The time to live (TTL) in milliseconds before the key will expire. Default is a week.'
            )
            .optional()
            .nullable(),
        })
        .describe('Idempotency information for the event.')
        .optional(),
    })
    .optional(),
});
export const ReportEventResponse = z.object({
  activationIds: z.array(z.string()).max(100).optional(),
});
export const BulkReportEventRequest = z.object({
  triggerKey: z
    .string()
    .describe(
      "Trigger key as defined in your app's trigger configuration\nin the app dashboard.\nFor example, `form_submitted` or `invoice_due`."
    )
    .min(1)
    .max(100),
  eventsInfo: z
    .array(
      z.object({
        payload: z
          .record(z.string(), z.any())
          .describe(
            'Event payload, formatted as key:value pairs.\nMust comply with the payload schema\nif you provided one when configuring your trigger.'
          )
          .optional()
          .nullable(),
        externalEntityId: z
          .string()
          .describe('ID of the related resource in GUID format.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        idempotency: z
          .object({
            key: z
              .string()
              .describe(
                'A unique identifier for the event.\nIf you send the same idempotency key in multiple report event requests, for the same trigger key and app id,\nconsecutive requests will be ignored after the first one. Note that the idempotency key is kept for a week before it expires.'
              )
              .min(1)
              .max(80)
              .optional(),
            ttlInMilliseconds: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Optional. The time to live (TTL) in milliseconds before the key will expire. Default is a week.'
              )
              .optional()
              .nullable(),
          })
          .describe('Idempotency information for the event.')
          .optional(),
      })
    )
    .min(1)
    .max(100),
});
export const BulkReportEventResponse = z.object({
  triggerKey: z
    .string()
    .describe('Trigger key associated with the event.')
    .min(1)
    .max(100)
    .optional(),
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for the individual item in the request.')
          .optional(),
        eventInfo: z
          .object({
            payload: z
              .record(z.string(), z.any())
              .describe(
                'Event payload, formatted as key:value pairs.\nMust comply with the payload schema\nif you provided one when configuring your trigger.'
              )
              .optional()
              .nullable(),
            externalEntityId: z
              .string()
              .describe('ID of the related resource in GUID format.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            idempotency: z
              .object({
                key: z
                  .string()
                  .describe(
                    'A unique identifier for the event.\nIf you send the same idempotency key in multiple report event requests, for the same trigger key and app id,\nconsecutive requests will be ignored after the first one. Note that the idempotency key is kept for a week before it expires.'
                  )
                  .min(1)
                  .max(80)
                  .optional(),
                ttlInMilliseconds: z
                  .string()
                  .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                  .describe(
                    'Optional. The time to live (TTL) in milliseconds before the key will expire. Default is a week.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Idempotency information for the event.')
              .optional(),
          })
          .describe('Event details for the item in the request.')
          .optional(),
        activationIds: z.array(z.string()).max(100).optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe(
      'Metadata for the overall bulk action, including success and failure counts.'
    )
    .optional(),
});
export const BulkCancelEventRequest = z.object({
  triggerKey: z
    .string()
    .describe(
      'Trigger key whose events you want to cancel.\nFor example, `form_submitted` or `invoice_due`.'
    )
    .min(1)
    .max(100),
  externalEntityIds: z.array(z.string()).min(1).max(100),
});
export const BulkCancelEventResponse = z.object({
  triggerKey: z
    .string()
    .describe('Trigger key related to the canceled event.')
    .min(1)
    .max(100)
    .optional(),
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe(
            'Metadata of the item, including its ID and success or failure status.'
          )
          .optional(),
        externalEntityId: z
          .string()
          .describe('ID of the related resource in GUID format.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe(
      'Metadata for the overall bulk action, including success and failure counts.'
    )
    .optional(),
});
export const CancelEventRequest = z.object({
  triggerKey: z
    .string()
    .describe(
      'Trigger key whose event you want to cancel.\nFor example, `form_submitted` or `invoice_due`.'
    )
    .min(1)
    .max(100),
  externalEntityId: z
    .string()
    .describe(
      'ID of the related resource in GUID format.\nFor example, `fc81a355-3429-50fc-a4c7-def486e828f3`.\n\nTypically, this ID is defined in your system,\nbut you can also use any Wix resource GUID,\nsuch as contact ID, member ID, or invoice ID.\nSee\n[Choose the right `externalEntityId`](https://dev.wix.com/docs/rest/business-management/automations/triggered-events/reporting-and-canceling-events#choose-the-right-externalentityid)\nfor more information.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const CancelEventResponse = z.object({});
export const ReportActionInvocationCompletedRequest = z.object({
  status: z.enum(['UNKNOWN_STATUS', 'SUCCESS', 'FAILURE']),
  invocationToken: z
    .string()
    .describe(
      "Invocation token from Wix's original call to [Invoke](https://dev.wix.com/docs/api-reference/business-management/automations/actions/action-provider-service-plugin/invoke). Wix uses this token to match your results with the correct automation run.\n\nLearn more about [running an invoked action with a timeout](https://dev.wix.com/docs/api-reference/business-management/automations/actions/action-provider-service-plugin/sample-flow#run-an-invoked-action-with-a-timeout)."
    )
    .max(2048),
  options: z
    .intersection(
      z.object({}),
      z.xor([
        z.object({
          successInfo: z.never().optional(),
          failureInfo: z.never().optional(),
        }),
        z.object({
          failureInfo: z.never().optional(),
          successInfo: z
            .object({
              result: z
                .record(z.string(), z.any())
                .describe(
                  "Your action's results as specified in its [output schema](https://dev.wix.com/docs/api-reference/business-management/automations/actions/about-actions#the-output-schema)."
                )
                .optional()
                .nullable(),
            })
            .describe("When the `status` is `SUCCESS`, your action's results."),
        }),
        z.object({
          successInfo: z.never().optional(),
          failureInfo: z
            .object({
              errorDescription: z
                .string()
                .describe('Why the action failed.')
                .max(1000)
                .optional(),
            })
            .describe('When the `status` is `FAILURE`, why the action failed.'),
        }),
      ])
    )
    .optional(),
});
export const ReportActionInvocationCompletedResponse = z.object({});
